import React, { useEffect, useState } from 'react';
import { compose } from 'redux';
import { connect } from 'react-redux';
import classNames from 'classnames';
import { withViewport } from '../../util/uiHelpers';

import { useConfiguration } from '../../context/configurationContext';
import { useViewabilityTracking } from '../../hooks/useViewabilityTracking';
import { useTrackEvent } from '../../context/AnalyticsContext';
import IconSocialMediaInstagram from '../../components/IconSocialMediaInstagram/IconSocialMediaInstagram';

import { FormattedMessage, useIntl } from '../../util/reactIntl';
import { REVIEW_TYPE_OF_PROVIDER, REVIEW_TYPE_OF_CUSTOMER, propTypes } from '../../util/types';
import {
  NO_ACCESS_PAGE_USER_PENDING_APPROVAL,
  NO_ACCESS_PAGE_VIEW_LISTINGS,
  PROFILE_PAGE_PENDING_APPROVAL_VARIANT,
} from '../../util/urlHelpers';
import {
  isErrorNoViewingPermission,
  isErrorUserPendingApproval,
  isForbiddenError,
  isNotFoundError,
} from '../../util/errors';
import {
  getCurrentUserTypeRoles,
  hasPermissionToViewData,
  isUserAuthorized,
} from '../../util/userHelpers';
import { richText } from '../../util/richText';

import { isScrollingDisabled } from '../../ducks/ui.duck';
import { getMarketplaceEntities } from '../../ducks/marketplaceData.duck';
import { toggleInWishlist } from '../../ducks/user.duck';
import { getArtistUsernamesData } from '../../ducks/artistUsernames.duck';
import {
  Heading,
  H2,
  H4,
  Page,
  AvatarLarge,
  NamedLink,
  ListingCard,
  Reviews,
  ButtonTabNavHorizontal,
  LayoutSideNavigation,
  ResponsiveImage,
  NamedRedirect,
} from '../../components';

import TopbarContainer from '../../containers/TopbarContainer/TopbarContainer';
import FooterContainer from '../../containers/FooterContainer/FooterContainer';
import NotFoundPage from '../../containers/NotFoundPage/NotFoundPage';

import css from './ProfilePage.module.css';

const MAX_MOBILE_SCREEN_WIDTH = 768;
const MIN_LENGTH_FOR_LONG_WORDS = 20;

const ExpandableBio = ({ bio }) => {
  const [isExpanded, setIsExpanded] = useState(false);
  const [shouldShowButton, setShouldShowButton] = useState(false);
  const bioRef = React.useRef(null);

  useEffect(() => {
    // Check if content exceeds max height
    if (bioRef.current) {
      setShouldShowButton(bioRef.current.scrollHeight > 100);
    }
  }, [bio]);

  const toggleExpand = () => {
    setIsExpanded(!isExpanded);
  };

  if (!bio) return null;

  return (
    <div className={classNames(css.bioWrapper, {
      [css.bioWrapperCollapsed]: !isExpanded,
      [css.bioWrapperExpanded]: isExpanded,
    })}>
      <p ref={bioRef} className={css.bio}>
        {bio}
      </p>
      {!isExpanded && shouldShowButton && <div className={css.fadeOut} />}
      {shouldShowButton && (
        <button onClick={toggleExpand} className={css.seeMoreButton}>
          <FormattedMessage
            id={isExpanded ? "ProfilePage.showLess" : "ProfilePage.showMore"}
            defaultMessage={isExpanded ? "Show less" : "Show more"}
          />
        </button>
      )}
    </div>
  );
};

const PromoCodeBanner = ({ promoCode }) => {
  if (!promoCode) return null;

  return (
    <div className={css.promoBanner}>
      <div className={css.promoBannerTag}>
        <svg className={css.promoBannerIcon} viewBox="0 0 20 20" fill="currentColor">
          <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm1-12a1 1 0 10-2 0v4a1 1 0 00.293.707l2.828 2.829a1 1 0 101.415-1.415L11 9.586V6z" clipRule="evenodd" />
        </svg>
        LIMITED TIME
      </div>
      <div className={css.promoBannerContent}>
        <span className={css.promoBannerOffer}>
          {promoCode.percentageOff}% Off All Items
        </span>
        <div className={css.promoBannerCodeSection}>
          <span className={css.promoBannerCodeLabel}>Use Code:</span>
          <span className={css.promoBannerCode}>{promoCode.code}</span>
        </div>
      </div>
    </div>
  );
};

// ExpandableBio.propTypes = {
//   bio: string,
// };

export const AsideContent = props => {
  const { user, displayName, isCurrentUser, bio, showLinkToProfileSettingsPage, reviewCount } = props;
  const { publicData, metadata, userFieldConfig, intl } = props;
  const hasBio = !!bio;
  return (
    <div className={css.asideContent}>
      {user && user.attributes.profile.publicData.banner ? (
        <div className={css.porfileTopbar}>
          <div className={css.bannerContainer}>
            <img src={user.attributes.profile.publicData.banner} className={css.bannerImage} />
          </div>
          <div className={css.avatarContainer}>
            <AvatarLarge className={css.avatar} user={user} disableProfileLink />
          </div>
        </div>
      ) : (
        <div className={css.porfileTopbar}>
          <div className={css.bannerContainer}>
          </div>
          <div className={css.avatarContainer}>
            <AvatarLarge className={css.avatar} user={user} disableProfileLink />
          </div>
        </div>
      )}

      <div className={css.profileCard}>
        <H2 as="h1" className={css.mobileHeading}>
          {displayName ? (
            <div className="flex items-center justify-between gap-2">
              <FormattedMessage id="ProfilePage.mobileHeading" values={{ name: displayName }} />
              {reviewCount > 0 ? (
                <a
                  href="#profile-reviews"
                  className="text-xs text-gray-500 hover:text-gray-700 border border-gray-300 rounded-full px-2 py-0.5 whitespace-nowrap flex-shrink-0"
                >
                  Reviews ({reviewCount})
                </a>
              ) : null}
            </div>
          ) : null}
          <div className="flex items-center gap-4 mt-2 mb-2">
            {publicData?.whatYouSell && (
              <p className="text-sm m-0 p-0 bg-gray-100 rounded-full px-2 py-1">
                {publicData.whatYouSell}
              </p>
            )}
            {publicData?.locationCity && (
              <p className="text-sm m-0 p-0 flex items-center gap-1 bg-gray-100 rounded-full px-2 py-1">
                <svg className="w-4 h-4" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor">
                  <path fillRule="evenodd" d="m9.69 18.933.003.001C9.89 19.02 10 19 10 19s.11.02.308-.066l.002-.001.006-.003.018-.008a5.741 5.741 0 0 0 .281-.14c.186-.096.446-.24.757-.433.62-.384 1.445-.966 2.274-1.765C15.302 14.988 17 12.493 17 9A7 7 0 1 0 3 9c0 3.492 1.698 5.988 3.355 7.584a13.731 13.731 0 0 0 2.273 1.765 11.842 11.842 0 0 0 .976.544l.062.029.018.008.006.003ZM10 11.25a2.25 2.25 0 1 0 0-4.5 2.25 2.25 0 0 0 0 4.5Z" clipRule="evenodd" />
                </svg>
                {publicData.locationCity}
              </p>
            )}
          </div>
          {hasBio ? <ExpandableBio bio={bio} /> : null}
          {displayName ? (
            <CustomUserFields
              publicData={publicData}
              metadata={metadata}
              userFieldConfig={userFieldConfig}
              intl={intl}
            />
          ) : null}
        </H2>
        {showLinkToProfileSettingsPage ? (
          <>
            <NamedLink className={css.editLinkMobile} name="ProfileSettingsPage">
              <FormattedMessage id="ProfilePage.editProfileLinkMobile" />
            </NamedLink>
            <NamedLink className={css.editLinkDesktop} name="ProfileSettingsPage">
              <FormattedMessage id="ProfilePage.editProfileLinkDesktop" />
            </NamedLink>
          </>
        ) : null}
      </div>
    </div>
  );
};

export const ReviewsErrorMaybe = props => {
  const { queryReviewsError } = props;
  return queryReviewsError ? (
    <p className={css.error}>
      <FormattedMessage id="ProfilePage.loadingReviewsFailed" />
    </p>
  ) : null;
};

export const MobileReviews = props => {
  const { reviews, queryReviewsError, userTypeRoles } = props;
  const { customer: isCustomerUserType, provider: isProviderUserType } = userTypeRoles;

  const reviewsOfProvider = reviews.filter(r => r.attributes.type === REVIEW_TYPE_OF_PROVIDER);
  const reviewsOfCustomer = reviews.filter(r => r.attributes.type === REVIEW_TYPE_OF_CUSTOMER);

  return (
    <div id="profile-reviews" className={css.mobileReviews}>
      {isProviderUserType && (
        <>
          <H4 as="h2" className={css.mobileReviewsTitle}>
            <FormattedMessage
              id="ProfilePage.reviewsFromMyCustomersTitle"
              values={{ count: reviewsOfProvider.length }}
            />
          </H4>
          <ReviewsErrorMaybe queryReviewsError={queryReviewsError} />
          <Reviews reviews={reviewsOfProvider} />
        </>
      )}
      {isCustomerUserType && (
        <>
          <H4 as="h2" className={css.mobileReviewsTitle}>
            <FormattedMessage
              id="ProfilePage.reviewsAsACustomerTitle"
              values={{ count: reviewsOfCustomer.length }}
            />
          </H4>
          <ReviewsErrorMaybe queryReviewsError={queryReviewsError} />
          <Reviews reviews={reviewsOfCustomer} />
        </>
      )}
    </div>
  );
};

export const DesktopReviews = props => {
  const { reviews, queryReviewsError, userTypeRoles, intl } = props;
  const { customer: isCustomerUserType, provider: isProviderUserType } = userTypeRoles;

  const initialReviewState = !isProviderUserType
    ? REVIEW_TYPE_OF_CUSTOMER
    : REVIEW_TYPE_OF_PROVIDER;
  const [showReviewsType, setShowReviewsType] = useState(initialReviewState);

  const reviewsOfProvider = reviews.filter(r => r.attributes.type === REVIEW_TYPE_OF_PROVIDER);
  const reviewsOfCustomer = reviews.filter(r => r.attributes.type === REVIEW_TYPE_OF_CUSTOMER);
  const isReviewTypeProviderSelected = showReviewsType === REVIEW_TYPE_OF_PROVIDER;
  const isReviewTypeCustomerSelected = showReviewsType === REVIEW_TYPE_OF_CUSTOMER;
  const providerReviewsMaybe = isProviderUserType
    ? [
      {
        text: (
          <Heading as="h3" rootClassName={css.desktopReviewsTitle}>
            <FormattedMessage
              id="ProfilePage.reviewsFromMyCustomersTitle"
              values={{ count: reviewsOfProvider.length }}
            />
          </Heading>
        ),
        selected: isReviewTypeProviderSelected,
        onClick: () => setShowReviewsType(REVIEW_TYPE_OF_PROVIDER),
      },
    ]
    : [];

  const customerReviewsMaybe = isCustomerUserType
    ? [
      {
        text: (
          <Heading as="h3" rootClassName={css.desktopReviewsTitle}>
            <FormattedMessage
              id="ProfilePage.reviewsAsACustomerTitle"
              values={{ count: reviewsOfCustomer.length }}
            />
          </Heading>
        ),
        selected: isReviewTypeCustomerSelected,
        onClick: () => setShowReviewsType(REVIEW_TYPE_OF_CUSTOMER),
      },
    ]
    : [];
  const desktopReviewTabs = [...providerReviewsMaybe, ...customerReviewsMaybe];

  return (
    <div id="profile-reviews" className={css.desktopReviews}>
      <div className={css.desktopReviewsWrapper}>
        <ButtonTabNavHorizontal
          className={css.desktopReviewsTabNav}
          tabs={desktopReviewTabs}
          ariaLabel={intl.formatMessage({ id: 'ProfilePage.screenreader.reviewsNav' })}
        />

        <ReviewsErrorMaybe queryReviewsError={queryReviewsError} />

        {isReviewTypeProviderSelected ? (
          <Reviews reviews={reviewsOfProvider} />
        ) : (
          <Reviews reviews={reviewsOfCustomer} />
        )}
      </div>
    </div>
  );
};

const WebsiteLink = ({ url }) => {
  if (!url) return null;

  // Format URL for display (remove http/https and trailing slashes)
  const displayUrl = url.replace(/^https?:\/\//, '').replace(/\/$/, '');

  return (
    <a href={url} target="_blank" rel="noopener noreferrer" className={css.externalLink}>
      <svg
        xmlns="http://www.w3.org/2000/svg"
        viewBox="0 0 20 20"
        fill="currentColor"
        className={css.icon}
      >
        <path d="M12.232 4.232a2.5 2.5 0 0 1 3.536 3.536l-1.225 1.224a.75.75 0 0 0 1.061 1.06l1.224-1.224a4 4 0 0 0-5.656-5.656l-3 3a4 4 0 0 0 .225 5.865.75.75 0 0 0 .977-1.138 2.5 2.5 0 0 1-.142-3.667l3-3Z" />
        <path d="M11.603 7.963a.75.75 0 0 0-.977 1.138 2.5 2.5 0 0 1 .142 3.667l-3 3a2.5 2.5 0 0 1-3.536-3.536l1.225-1.224a.75.75 0 0 0-1.061-1.06l-1.224 1.224a4 4 0 1 0 5.656 5.656l3-3a4 4 0 0 0-.225-5.865Z" />
      </svg>
      <span className={css.linkText}>{displayUrl}</span>
    </a>
  );
};

// Social Media Link Component
const SocialMediaLink = ({ platform, link }) => {
  if (!link) return null;

  const socialMediaConfig = {
    instagram: { icon: IconSocialMediaInstagram },
  };

  const { icon: Icon } = socialMediaConfig[platform] || {};
  if (!Icon) return null;

  return (
    <a href={link} target="_blank" rel="noopener noreferrer" className={css.externalLink}>
      <Icon size={20} className="mr-2" />
    </a>
  );
};

// Profile Links Component
const CustomUserFields = ({ publicData }) => {
  const { website, github, twitter, facebook, instagram, linkedin } = publicData || {};

  return (
    <div className={css.userFields}>
      <WebsiteLink url={website} />
      <SocialMediaLink platform="instagram" link={instagram} />
    </div>
  );
};



export const MainContent = props => {
  const [mounted, setMounted] = useState(false);
  const { trackEvent } = useTrackEvent();

  useEffect(() => {
    setMounted(true);
  }, []);

  const {
    userShowError,
    bio,
    displayName,
    listings,
    user,
    queryListingsError,
    reviews = [],
    queryReviewsError,
    publicData,
    metadata,
    userFieldConfig,
    intl,
    hideReviews,
    currentUser,
    onToggleInWishlist,
    userTypeRoles,
    publicPromoCode,
  } = props;

  // Track viewable impressions for profile listings
  const profileTrackingData = {
    artist_id: user?.id?.uuid,
    artist_name: displayName,
    total_artist_listings: listings.length,
    profile_view_session: Date.now()
  };

  useViewabilityTracking(listings, 'artist_profile', profileTrackingData);

  // Handle listing clicks from profile
  const handleProfileListingClick = (listing, position, eventType = 'click') => {
    const baseEventData = {
      listing_id: listing.id.uuid,
      listing_type: listing.attributes.publicData.listingType,
      click_position: position + 1,
      artist_id: listing.author?.id?.uuid,
      artist_name: displayName,
      price: listing.attributes.price?.amount,
      currency: listing.attributes.price?.currency,
      source: 'artist_profile',
      total_artist_listings: listings.length,
      timestamp: new Date().toISOString()
    };

    if (eventType === 'click') {
      trackEvent('listing_click', baseEventData);
    } else if (eventType === 'detail_view') {
      trackEvent('listing_detail_view', {
        ...baseEventData,
        view_source: 'artist_profile_click'
      });
    }
  };

  const hasListings = listings.length > 0;
  const hasMatchMedia = typeof window !== 'undefined' && window?.matchMedia;
  const isMobileLayout =
    mounted && hasMatchMedia
      ? window.matchMedia(`(max-width: ${MAX_MOBILE_SCREEN_WIDTH}px)`)?.matches
      : true;

  const hasBio = !!bio;
  const bioWithLinks = richText(bio, {
    linkify: true,
    longWordMinLength: MIN_LENGTH_FOR_LONG_WORDS,
    longWordClass: css.longWord,
  });

  const listingsContainerClasses = classNames(css.listingsContainer, {
    [css.withBioMissingAbove]: !hasBio,
  });

  if (userShowError || queryListingsError) {
    return (
      <p className={css.error}>
        <FormattedMessage id="ProfilePage.loadingDataFailed" />
      </p>
    );
  }
  return (
    <div>
      <PromoCodeBanner promoCode={publicPromoCode} />
      <H4 as="h3" className={css.desktopHeading}>
        <FormattedMessage id="ProfilePage.desktopHeading" values={{ name: displayName }} />
      </H4>
      {hasListings ? (
        <div className={listingsContainerClasses}>
          <ul className={css.listings}>
            {listings.map((l, index) => (
              <li className={css.listing} key={l.id.uuid}>
                <ListingCard
                  listing={l}
                  position={index}
                  source="artist_profile"
                  trackingData={profileTrackingData}
                  showAuthorInfo={false}
                  currentUser={currentUser}
                  onToggleWishlist={onToggleInWishlist}
                  onClick={(listing, position, eventType) => handleProfileListingClick(listing, position, eventType)}
                  data-listing-id={l.id.uuid}
                  data-position={index + 1}
                />
              </li>
            ))}
          </ul>
        </div>
      ) : null}
      {hideReviews ? null : isMobileLayout ? (
        <MobileReviews
          reviews={reviews}
          queryReviewsError={queryReviewsError}
          userTypeRoles={userTypeRoles}
        />
      ) : (
        <DesktopReviews
          reviews={reviews}
          queryReviewsError={queryReviewsError}
          userTypeRoles={userTypeRoles}
          intl={intl}
        />
      )}

    </div>
  );
};

/**
 * ProfilePageComponent
 *
 * @component
 * @param {Object} props
 * @param {boolean} props.scrollingDisabled - Whether the scrolling is disabled
 * @param {propTypes.currentUser} props.currentUser - The current user
 * @param {boolean} props.useCurrentUser - Whether to use the current user
 * @param {propTypes.user|propTypes.currentUser} props.user - The user
 * @param {propTypes.error} props.userShowError - The user show error
 * @param {propTypes.error} props.queryListingsError - The query listings error
 * @param {Array<propTypes.listing|propTypes.ownListing>} props.listings - The listings
 * @param {Array<propTypes.review>} props.reviews - The reviews
 * @param {propTypes.error} props.queryReviewsError - The query reviews error
 * @returns {JSX.Element} ProfilePageComponent
 */
export const ProfilePageComponent = props => {
  const config = useConfiguration();
  const intl = useIntl();
  const [mounted, setMounted] = useState(false);

  useEffect(() => {
    setMounted(true);
  }, []);

  const {
    scrollingDisabled,
    params: pathParams,
    currentUser,
    useCurrentUser,
    userShowError,
    user,
    onToggleInWishlist,
    reviews,
    artistUsernamesData,
    ...rest
  } = props;
  const isVariant = pathParams.variant?.length > 0;
  const isPreview = isVariant && pathParams.variant === PROFILE_PAGE_PENDING_APPROVAL_VARIANT;

  // Stripe's onboarding needs a business URL for each seller, but the profile page can be
  // too empty for the provider at the time they are creating their first listing.
  // To remedy the situation, we redirect Stripe's crawler to the landing page of the marketplace.
  // TODO: When there's more content on the profile page, we should consider by-passing this redirection.
  const searchParams = rest?.location?.search;
  const isStorefront = searchParams
    ? new URLSearchParams(searchParams)?.get('mode') === 'storefront'
    : false;
  if (isStorefront) {
    return <NamedRedirect name="LandingPage" />;
  }

  const isCurrentUser = currentUser?.id && currentUser?.id?.uuid === pathParams.id;
  const profileUser = useCurrentUser ? currentUser : user;
  const { bio, displayName, publicData, metadata } = profileUser?.attributes?.profile || {};
  const { userFields } = config.user;

  // Get public promo code from artistUsernames data
  const publicPromoCode = profileUser?.id?.uuid && artistUsernamesData
    ? artistUsernamesData[profileUser.id.uuid]?.publicPromoCode || null
    : null;
  const isPrivateMarketplace = config.accessControl.marketplace.private === true;
  const isUnauthorizedUser = currentUser && !isUserAuthorized(currentUser);
  const isUnauthorizedOnPrivateMarketplace = isPrivateMarketplace && isUnauthorizedUser;
  const hasUserPendingApprovalError = isErrorUserPendingApproval(userShowError);
  const hasNoViewingRightsUser = currentUser && !hasPermissionToViewData(currentUser);
  const hasNoViewingRightsOnPrivateMarketplace = isPrivateMarketplace && hasNoViewingRightsUser;

  const userTypeRoles = getCurrentUserTypeRoles(config, profileUser);

  const isDataLoaded = isPreview
    ? currentUser != null || userShowError != null
    : hasNoViewingRightsOnPrivateMarketplace
      ? currentUser != null || userShowError != null
      : user != null || userShowError != null;

  const schemaTitleVars = { name: displayName, marketplaceName: config.marketplaceName };
  const schemaTitle = intl.formatMessage({ id: 'ProfilePage.schemaTitle' }, schemaTitleVars);

  if (!isDataLoaded) {
    return null;
  } else if (!isPreview && isNotFoundError(userShowError)) {
    return <NotFoundPage staticContext={props.staticContext} />;
  } else if (!isPreview && (isUnauthorizedOnPrivateMarketplace || hasUserPendingApprovalError)) {
    return (
      <NamedRedirect
        name="NoAccessPage"
        params={{ missingAccessRight: NO_ACCESS_PAGE_USER_PENDING_APPROVAL }}
      />
    );
  } else if (
    (!isPreview && hasNoViewingRightsOnPrivateMarketplace && !isCurrentUser) ||
    isErrorNoViewingPermission(userShowError)
  ) {
    // Someone without viewing rights on a private marketplace is trying to
    // view a profile page that is not their own – redirect to NoAccessPage
    return (
      <NamedRedirect
        name="NoAccessPage"
        params={{ missingAccessRight: NO_ACCESS_PAGE_VIEW_LISTINGS }}
      />
    );
  } else if (!isPreview && isForbiddenError(userShowError)) {
    // This can happen if private marketplace mode is active, but it's not reflected through asset yet.
    return (
      <NamedRedirect
        name="SignupForUserTypePage"
        params={{ userType: 'customer' }}
        state={{ from: `${location.pathname}${location.search}${location.hash}` }}
      />
    );
  } else if (isPreview && mounted && !isCurrentUser) {
    // Someone is manipulating the URL, redirect to current user's profile page.
    return isCurrentUser === false ? (
      <NamedRedirect name="ProfilePage" params={{ id: currentUser?.id?.uuid }} />
    ) : null;
  } else if ((isPreview || isPrivateMarketplace) && !mounted) {
    // This preview of the profile page is not rendered on server-side
    // and the first pass on client-side should render the same UI.
    return null;
  }

  // This is rendering normal profile page (not preview for pending-approval)
  return (
    <Page
      scrollingDisabled={scrollingDisabled}
      title={schemaTitle}
      schema={{
        '@context': 'http://schema.org',
        '@type': 'ProfilePage',
        mainEntity: {
          '@type': 'Person',
          name: profileUser?.attributes?.profile?.displayName,
        },
        name: schemaTitle,
      }}
    >
      <LayoutSideNavigation
        sideNavClassName={css.aside}
        topbar={<TopbarContainer />}
        sideNav={
          <AsideContent bio={bio} intl={intl} metadata={metadata} user={user} userFieldConfig={userFields} publicData={publicData} isCurrentUser={isCurrentUser} displayName={displayName} reviewCount={reviews.filter(r => r.attributes.type === REVIEW_TYPE_OF_PROVIDER).length} />

        }
        footer={<FooterContainer />}
      >
        <MainContent
          bio={bio}
          displayName={displayName}
          userShowError={userShowError}
          metadata={metadata}
          userFieldConfig={userFields}
          hideReviews={reviews.length === 0}
          intl={intl}
          reviews={reviews}
          currentUser={currentUser}
          onToggleInWishlist={onToggleInWishlist}
          userTypeRoles={userTypeRoles}
          publicPromoCode={publicPromoCode}
          {...rest}
        />
      </LayoutSideNavigation>
    </Page>
  );
};

const mapStateToProps = state => {
  const { currentUser } = state.user;
  const {
    userId,
    userShowError,
    queryListingsError,
    userListingRefs,
    reviews = [],
    queryReviewsError,
  } = state.ProfilePage;
  const userMatches = getMarketplaceEntities(state, [{ type: 'user', id: userId }]);
  const user = userMatches.length === 1 ? userMatches[0] : null;

  // Show currentUser's data if it's not approved yet
  const isCurrentUser = userId?.uuid === currentUser?.id?.uuid;
  const useCurrentUser =
    isCurrentUser && !(isUserAuthorized(currentUser) && hasPermissionToViewData(currentUser));

  return {
    scrollingDisabled: isScrollingDisabled(state),
    currentUser,
    useCurrentUser,
    user,
    userShowError,
    queryListingsError,
    listings: getMarketplaceEntities(state, userListingRefs),
    reviews,
    queryReviewsError,
    artistUsernamesData: getArtistUsernamesData(state),
  };
};

const mapDispatchToProps = dispatch => ({
  onToggleInWishlist: listingId => dispatch(toggleInWishlist(listingId)),
});

// Note: it is important that the withRouter HOC is **outside** the
// connect HOC, otherwise React Router won't rerender any Route
// components since connect implements a shouldComponentUpdate
// optimization.
const ProfilePage = compose(
  connect(
    mapStateToProps,
    mapDispatchToProps
  ),
  withViewport
)(ProfilePageComponent);

export default ProfilePage;
